import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Send, Loader2, Moon, MessageCircle, Shield, ArrowRight,
  Search, Sparkles, AlertTriangle, Heart, Phone, ChevronDown,
  ChevronUp, Clock, Activity
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════
// NATURAL LANGUAGE COMMAND CENTER
// ═══════════════════════════════════════════════════════
function NLCommandCenter() {
  const [query, setQuery] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const suggestions = [
    "Show me all patients with pain above 7",
    "Which patients are declining the fastest?",
    "How many admissions are in the pipeline?",
    "Who has the highest PRN medication usage?",
    "What's our current census breakdown by diagnosis?",
    "Are there any active critical alerts right now?",
  ];

  const ask = async (q) => {
    const question = q || query;
    if (!question.trim()) return;
    setLoading(true);
    setHistory(prev => [...prev, { type: 'question', text: question }]);
    setQuery('');
    try {
      const res = await fetch(`${API}/api/wave1/command-center/ask`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: question }),
      });
      const data = await res.json();
      setHistory(prev => [...prev, {
        type: 'answer', text: data.answer || 'No response',
        meta: { patients: data.patient_count, alerts: data.alert_count }
      }]);
    } catch {
      setHistory(prev => [...prev, { type: 'answer', text: 'Unable to process query. Please try again.', error: true }]);
    }
    setLoading(false);
  };

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  return (
    <div className="flex flex-col h-full" data-testid="nl-command-center">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-sky-300" />
        </div>
        <div>
          <h2 className="text-lg font-heading font-semibold text-[#2C3D30]">AI Command Center</h2>
          <p className="text-xs text-[#5C6A5F]">Ask anything about your operations</p>
        </div>
      </div>

      {history.length === 0 && (
        <div className="mb-5">
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => ask(s)}
                className="text-xs px-4 py-2 rounded-full bg-sand-100 hover:bg-sky-50 hover:text-sky-500 text-[#5C6A5F] transition-all duration-300 border border-sand-200"
                data-testid={`nl-suggestion-${i}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-3 mb-5 min-h-[200px] max-h-[500px]">
        {history.map((item, i) => (
          <div key={i} className={`flex ${item.type === 'question' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-5 py-3 text-sm ${
              item.type === 'question'
                ? 'bg-sky-300 text-white'
                : item.error
                  ? 'bg-terra-50 text-terra-500 border border-terra-200'
                  : 'bg-sand-100 text-[#2C3D30]'
            }`}>
              <p className="whitespace-pre-wrap">{item.text}</p>
              {item.meta && (
                <div className="flex gap-3 mt-2 pt-2 border-t border-sand-200/50">
                  <span className="text-xs opacity-70">Census: {item.meta.patients}</span>
                  <span className="text-xs opacity-70">Alerts: {item.meta.alerts}</span>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-sand-100 rounded-2xl px-5 py-3 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-sky-300" />
              <span className="text-sm text-[#5C6A5F]">Analyzing operations data...</span>
            </div>
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      <div className="flex gap-2">
        <Input value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && ask()}
          placeholder="Ask about patients, staff, compliance, scheduling..."
          className="flex-1 rounded-xl border-sand-200 bg-sand-50 focus:border-sage-400 focus:ring-sage-400" disabled={loading} data-testid="nl-query-input" />
        <Button onClick={() => ask()} disabled={loading || !query.trim()}
          className="rounded-full bg-sage-400 hover:bg-sage-500 px-4"
          data-testid="nl-query-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// IS THIS NORMAL? AI COMPANION
// ═══════════════════════════════════════════════════════
function IsThisNormal({ patients }) {
  const [selectedPatient, setSelectedPatient] = useState('');
  const [description, setDescription] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const urgencyConfig = {
    emergency: { label: 'CALL 911', bg: 'bg-terra-400', text: 'text-white', border: 'border-terra-400' },
    call_nurse: { label: 'Call the Nurse', bg: 'bg-terra-300', text: 'text-white', border: 'border-terra-300' },
    monitor: { label: 'Worth Monitoring', bg: 'bg-sky-50', text: 'text-sky-500', border: 'border-sky-200' },
    expected: { label: 'Expected', bg: 'bg-sage-50', text: 'text-sage-400', border: 'border-sage-200' },
  };

  const askAI = async () => {
    if (!selectedPatient || !description.trim()) return;
    setLoading(true);
    setResponse(null);
    try {
      const res = await fetch(`${API}/api/wave1/is-this-normal/ask`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: selectedPatient, description }),
      });
      const data = await res.json();
      setResponse(data);
      setHistory(prev => [data, ...prev]);
      setDescription('');
    } catch {
      setResponse({ ai_response: 'Unable to connect. Please call your Goodwin Cares team directly.', urgency: 'call_nurse' });
    }
    setLoading(false);
  };

  const escalate = async (queryId) => {
    try {
      await fetch(`${API}/api/wave1/is-this-normal/escalate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: selectedPatient, question_id: queryId }),
      });
      setResponse(prev => ({ ...prev, escalated: true }));
    } catch {}
  };

  return (
    <div data-testid="is-this-normal">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-terra-50 flex items-center justify-center">
          <Heart className="w-5 h-5 text-terra-400" />
        </div>
        <div>
          <h2 className="text-lg font-heading font-semibold text-[#2C3D30]">Is This Normal?</h2>
          <p className="text-xs text-[#5C6A5F]">Family AI Companion</p>
        </div>
      </div>

      <div className="space-y-3">
        <select value={selectedPatient} onChange={e => setSelectedPatient(e.target.value)}
          className="w-full rounded-xl border border-sand-200 px-4 py-2.5 text-sm bg-sand-50 focus:border-sage-400"
          data-testid="itn-patient-select">
          <option value="">Select your loved one...</option>
          {patients.map(p => (
            <option key={p.id} value={p.id}>{p.name} — {p.primary_diagnosis || 'N/A'}</option>
          ))}
        </select>

        <textarea value={description} onChange={e => setDescription(e.target.value)}
          placeholder="Describe what you're seeing... (e.g., 'Mom's breathing sounds different tonight')"
          className="w-full rounded-xl border border-sand-200 px-4 py-3 text-sm min-h-[80px] resize-none bg-sand-50 focus:border-sage-400"
          data-testid="itn-description" />

        <Button onClick={askAI} disabled={loading || !selectedPatient || !description.trim()}
          className="w-full rounded-full bg-terra-400 hover:bg-terra-500 text-white" data-testid="itn-submit">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Thinking...</>
            : <><MessageCircle className="w-4 h-4 mr-2" />Ask the AI Companion</>}
        </Button>
      </div>

      {response && (
        <div className={`mt-5 p-5 rounded-2xl bg-sand-50 border-l-4 ${urgencyConfig[response.urgency]?.border || 'border-sand-200'}`}
          data-testid="itn-response">
          <div className="flex items-center gap-2 mb-3">
            <Badge className={`${urgencyConfig[response.urgency]?.bg || 'bg-sand-200'} ${urgencyConfig[response.urgency]?.text || 'text-[#2C3D30]'} rounded-full px-3 py-1 text-xs`}>
              {urgencyConfig[response.urgency]?.label || response.urgency}
            </Badge>
          </div>
          <p className="text-sm text-[#2C3D30] leading-relaxed whitespace-pre-wrap">{response.ai_response}</p>
          {response.urgency !== 'expected' && !response.escalated && (
            <Button variant="outline" className="mt-4 rounded-full border-terra-200 text-terra-400 hover:bg-terra-50"
              onClick={() => escalate(response.id)} data-testid="itn-escalate-btn">
              <Phone className="w-3.5 h-3.5 mr-1.5" /> Send to On-Call Nurse
            </Button>
          )}
          {response.escalated && (
            <Badge className="mt-3 bg-sage-50 text-sage-400 rounded-full">Sent to nurse — they will call you shortly</Badge>
          )}
        </div>
      )}

      {history.length > 1 && (
        <button onClick={() => setShowHistory(!showHistory)}
          className="mt-4 text-xs text-[#5C6A5F] flex items-center gap-1 hover:text-[#2C3D30]">
          {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          Previous questions ({history.length - 1})
        </button>
      )}
      {showHistory && history.slice(1).map((h, i) => (
        <div key={i} className="mt-2 p-3 rounded-xl bg-sand-100 border border-sand-200">
          <p className="text-xs font-medium text-[#5C6A5F] mb-1">"{h.family_question}"</p>
          <Badge className={`text-xs rounded-full ${urgencyConfig[h.urgency]?.bg || 'bg-sand-200'} ${urgencyConfig[h.urgency]?.text || ''}`}>
            {urgencyConfig[h.urgency]?.label || h.urgency}
          </Badge>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// GOODNIGHT CHECK (with scheduler + email + SMS)
// ═══════════════════════════════════════════════════════
function GoodnightCheck({ patients }) {
  const [selectedPatient, setSelectedPatient] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [scheduleRunning, setScheduleRunning] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/wave1/goodnight-check/settings`)
      .then(r => r.json()).then(setSettings).catch(() => {});
  }, []);

  const generate = async () => {
    if (!selectedPatient) return;
    setLoading(true); setSummary(null);
    try {
      const res = await fetch(`${API}/api/wave1/goodnight-check/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: selectedPatient }),
      });
      setSummary(await res.json());
    } catch {
      setSummary({ summary: 'Unable to generate summary. Please try again.', error: true });
    }
    setLoading(false);
  };

  const generateAll = async () => {
    setBulkLoading(true); setBulkResults(null);
    try {
      const res = await fetch(`${API}/api/wave1/goodnight-check/bulk-generate`, { method: 'POST' });
      setBulkResults(await res.json());
    } catch { setBulkResults({ error: true }); }
    setBulkLoading(false);
  };

  const sendEmail = async () => {
    if (!summary?.id || !selectedPatient) return;
    setEmailSending(true);
    try {
      const res = await fetch(`${API}/api/wave1/goodnight-check/send-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: selectedPatient, check_id: summary.id }),
      });
      await res.json();
      setSummary(prev => ({ ...prev, emailed: true }));
    } catch {}
    setEmailSending(false);
  };

  const updateSettings = async (updates) => {
    const newSettings = { ...settings, ...updates };
    try {
      await fetch(`${API}/api/wave1/goodnight-check/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: newSettings.enabled, send_time: newSettings.send_time, email_enabled: newSettings.email_enabled }),
      });
      setSettings(newSettings);
    } catch {}
  };

  const runSchedule = async () => {
    setScheduleRunning(true);
    try {
      const res = await fetch(`${API}/api/wave1/goodnight-check/schedule-run`, { method: 'POST' });
      setBulkResults(await res.json());
    } catch {}
    setScheduleRunning(false);
  };

  return (
    <div data-testid="goodnight-check">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-sage-50 flex items-center justify-center">
          <Moon className="w-5 h-5 text-sage-400" />
        </div>
        <div>
          <h2 className="text-lg font-heading font-semibold text-[#2C3D30]">Goodnight Check</h2>
          <p className="text-xs text-[#5C6A5F]">Evening family summary</p>
        </div>
      </div>

      {settings && (
        <div className="mb-5 p-4 rounded-2xl bg-sage-50 border border-sage-100" data-testid="gn-scheduler-settings">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-sage-600 tracking-wide uppercase">Automated Schedule</span>
            <button onClick={() => updateSettings({ enabled: !settings.enabled })}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.enabled ? 'bg-sage-400' : 'bg-sand-300'}`}
              data-testid="gn-schedule-toggle">
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${settings.enabled ? 'translate-x-4' : 'translate-x-1'}`} />
            </button>
          </div>
          <div className="flex items-center gap-4 text-xs text-sage-600">
            <label className="flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> Send at:
              <input type="time" value={settings.send_time || '20:00'}
                onChange={e => updateSettings({ send_time: e.target.value })}
                className="ml-1 px-2 py-0.5 rounded-lg border border-sage-200 bg-white text-xs"
                data-testid="gn-send-time" />
            </label>
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={settings.email_enabled !== false}
                onChange={e => updateSettings({ email_enabled: e.target.checked })}
                className="rounded border-sage-300 text-sage-400" data-testid="gn-email-toggle" />
              Email to families
            </label>
          </div>
          {settings.last_run && (
            <p className="text-xs text-sage-500 mt-1.5">Last run: {new Date(settings.last_run).toLocaleString()}</p>
          )}
          <Button variant="outline" size="sm" onClick={runSchedule} disabled={scheduleRunning}
            className="mt-2 text-xs rounded-full border-sage-200 text-sage-600 hover:bg-sage-100"
            data-testid="gn-run-schedule-btn">
            {scheduleRunning ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Running...</> : 'Run Now (All Patients + Email)'}
          </Button>
        </div>
      )}

      <div className="flex gap-2 mb-3">
        <select value={selectedPatient} onChange={e => setSelectedPatient(e.target.value)}
          className="flex-1 rounded-xl border border-sand-200 px-4 py-2.5 text-sm bg-sand-50"
          data-testid="gn-patient-select">
          <option value="">Select patient...</option>
          {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <Button onClick={generate} disabled={loading || !selectedPatient}
          className="rounded-full bg-sage-400 hover:bg-sage-500"
          data-testid="gn-generate-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Moon className="w-4 h-4 mr-1.5" />Generate</>}
        </Button>
      </div>

      <Button variant="outline" onClick={generateAll} disabled={bulkLoading}
        className="w-full mb-3 text-xs rounded-full border-sand-200" data-testid="gn-bulk-btn">
        {bulkLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Generating...</> : 'Generate for All Active Patients'}
      </Button>

      {bulkResults && !bulkResults.error && (
        <div className="mb-3 p-3 rounded-xl bg-sage-50 border border-sage-100">
          <p className="text-xs text-sage-600">
            Generated {bulkResults.generated} summaries
            {bulkResults.errors > 0 && <span className="text-terra-400"> ({bulkResults.errors} errors)</span>}
          </p>
        </div>
      )}

      {summary && (
        <div className="p-5 rounded-2xl bg-sand-50 border-l-4 border-l-sage-400" data-testid="gn-summary">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-heading font-semibold text-[#2C3D30]">{summary.patient_name}</span>
            <div className="flex gap-2">
              {summary.pain_level != null && <Badge variant="outline" className="text-xs rounded-full border-sand-200">Pain: {summary.pain_level}/10</Badge>}
              {summary.pps_score != null && <Badge variant="outline" className="text-xs rounded-full border-sand-200">PPS: {summary.pps_score}</Badge>}
            </div>
          </div>
          <p className="text-sm text-[#2C3D30] leading-relaxed whitespace-pre-wrap">{summary.summary}</p>
          <div className="flex items-center justify-between mt-4">
            <p className="text-xs text-[#5C6A5F] flex items-center gap-1">
              <Clock className="w-3 h-3" />{new Date(summary.generated_at).toLocaleString()}
            </p>
            {!summary.emailed ? (
              <Button variant="outline" size="sm" onClick={sendEmail} disabled={emailSending}
                className="text-xs rounded-full border-sage-200 text-sage-600 hover:bg-sage-50"
                data-testid="gn-send-email-btn">
                {emailSending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                Email to Family
              </Button>
            ) : (
              <Badge className="bg-sage-50 text-sage-400 rounded-full text-xs">Emailed</Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════
export default function Wave1FeaturesPage() {
  const [patients, setPatients] = useState([]);
  const [activeTab, setActiveTab] = useState('command');

  useEffect(() => {
    fetch(`${API}/api/wave1/patients-list`)
      .then(r => r.ok ? r.json() : { patients: [] })
      .then(d => setPatients(d.patients || []))
      .catch(() => {});
  }, []);

  const tabs = [
    { id: 'command', label: 'AI Command Center', icon: Sparkles, color: 'sky' },
    { id: 'normal', label: 'Is This Normal?', icon: Heart, color: 'terra' },
    { id: 'goodnight', label: 'Goodnight Check', icon: Moon, color: 'sage' },
  ];

  const tabColors = {
    sky: 'bg-sky-50 text-sky-500 border border-sky-200',
    terra: 'bg-terra-50 text-terra-400 border border-terra-200',
    sage: 'bg-sage-50 text-sage-400 border border-sage-200',
  };

  return (
    <div className="min-h-screen bg-sand-100" data-testid="wave1-page">
      <div className="max-w-4xl mx-auto p-6 sm:p-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#2C3D30] tracking-tight">AI Experience Hub</h1>
          <p className="text-sm text-[#5C6A5F] mt-1">Intelligent tools for operations, families, and care coordination</p>
        </div>

        <div className="flex gap-2 mb-8 pb-4 border-b border-sand-200">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                activeTab === tab.id ? tabColors[tab.color] : 'text-[#5C6A5F] hover:text-[#2C3D30] hover:bg-sand-200'
              }`}
              data-testid={`wave1-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-sand-50 rounded-2xl border border-sand-200 p-6 sm:p-8">
          {activeTab === 'command' && <NLCommandCenter />}
          {activeTab === 'normal' && <IsThisNormal patients={patients} />}
          {activeTab === 'goodnight' && <GoodnightCheck patients={patients} />}
        </div>
      </div>
    </div>
  );
}
